/**
 * Webhook Management Dashboard
 * Provides comprehensive webhook registration, monitoring, and debugging interface
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Play,
  Pause,
  Trash2,
  Edit,
  Eye,
  TestTube,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Copy,
  ExternalLink,
  Search,
  Zap,
  Activity,
  BarChart3,
  Globe,
  Lock,
  Unlock,
  Bot,
  Key
} from 'lucide-react';

import { 
  webhookManager,
  WebhookEndpoint,
  WebhookDelivery,
  WebhookTestResult,
  WebhookEvent,
  WebhookRetryPolicy,
  WebhookFilter,
  WebhookTransformation
} from '../../services/webhooks/WebhookManager';
import { useConfirm } from "components/ui/confirm";
import { tone, toneForStatus } from "components/ui/tone";
import { SelectField } from "components/ui/select-field";

interface WebhookManagementDashboardProps {
  integrationId?: string;
  agentMode?: boolean; // New prop to indicate agent API integration
}

const WebhookManagementDashboard: React.FC<WebhookManagementDashboardProps> = ({
  integrationId,
  agentMode = false
}) => {
  const confirm = useConfirm();
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([]);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookEndpoint | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [_showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<WebhookDelivery | null>(null);
  const [activeTab, setActiveTab] = useState<'webhooks' | 'deliveries' | 'analytics' | 'agents'>('webhooks');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [testResult, setTestResult] = useState<WebhookTestResult | null>(null);
  const [testing, setTesting] = useState(false);

  // Form state for webhook creation/editing
  const [webhookForm, setWebhookForm] = useState({
    name: '',
    url: '',
    events: [] as string[],
    active: true,
    secret: '',
    headers: {} as Record<string, string>,
    retryPolicy: {
      maxAttempts: 5,
      backoffMultiplier: 2,
      initialDelay: 1000,
      maxDelay: 300000,
      retryOn: [408, 429, 500, 502, 503, 504]
    } as WebhookRetryPolicy,
    filters: [] as WebhookFilter[],
    transformations: [] as WebhookTransformation[]
  });

  // Available webhook events - enhanced for agent API
  const availableEvents: WebhookEvent[] = [
    // Integration events
    { type: 'integration.connected', description: 'Integration successfully connected' },
    { type: 'integration.disconnected', description: 'Integration disconnected' },
    { type: 'sync.started', description: 'Data synchronization started' },
    { type: 'sync.completed', description: 'Data synchronization completed' },
    { type: 'sync.failed', description: 'Data synchronization failed' },
    
    // Agent API events (new)
    { type: 'agent.registered', description: 'AI agent registered' },
    { type: 'agent.authenticated', description: 'AI agent authenticated' },
    { type: 'agent.rate_limited', description: 'AI agent hit rate limit' },
    { type: 'agent.request.success', description: 'Agent API request succeeded' },
    { type: 'agent.request.failed', description: 'Agent API request failed' },
    { type: 'agent.campaign.created', description: 'Agent created campaign' },
    { type: 'agent.campaign.launched', description: 'Agent launched campaign' },
    { type: 'agent.lead.created', description: 'Agent created lead' },
    { type: 'agent.email.sent', description: 'Agent sent email' },
    { type: 'agent.segment.created', description: 'Agent created segment' },
    
    // Standard events
    { type: 'contact.created', description: 'New contact created' },
    { type: 'contact.updated', description: 'Contact information updated' },
    { type: 'contact.deleted', description: 'Contact deleted' },
    { type: 'campaign.sent', description: 'Email campaign sent' },
    { type: 'campaign.opened', description: 'Email campaign opened' },
    { type: 'campaign.clicked', description: 'Email campaign link clicked' },
    { type: 'order.created', description: 'New order created (e-commerce)' },
    { type: 'order.updated', description: 'Order status updated (e-commerce)' },
    { type: 'cart.abandoned', description: 'Shopping cart abandoned (e-commerce)' },
    { type: 'error.occurred', description: 'Integration error occurred' }
  ];

  useEffect(() => {
    loadWebhooks();
    loadDeliveries();
  }, [integrationId]);

  useEffect(() => {
    // Set up real-time listeners
    const handleWebhookUpdate = () => {
      loadWebhooks();
    };

    const handleDeliveryUpdate = () => {
      loadDeliveries();
    };

    webhookManager.on('webhook_registered', handleWebhookUpdate);
    webhookManager.on('webhook_updated', handleWebhookUpdate);
    webhookManager.on('webhook_deleted', handleWebhookUpdate);
    webhookManager.on('delivery_created', handleDeliveryUpdate);
    webhookManager.on('delivery_succeeded', handleDeliveryUpdate);
    webhookManager.on('delivery_failed', handleDeliveryUpdate);

    return () => {
      webhookManager.off('webhook_registered', handleWebhookUpdate);
      webhookManager.off('webhook_updated', handleWebhookUpdate);
      webhookManager.off('webhook_deleted', handleWebhookUpdate);
      webhookManager.off('delivery_created', handleDeliveryUpdate);
      webhookManager.off('delivery_succeeded', handleDeliveryUpdate);
      webhookManager.off('delivery_failed', handleDeliveryUpdate);
    };
  }, []);

  const loadWebhooks = useCallback(async () => {
    try {
      setLoading(true);
      const webhookData = webhookManager.getWebhooks(integrationId);
      setWebhooks(webhookData);
    } catch (error) {
      console.error('Failed to load webhooks:', error);
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  const loadDeliveries = useCallback(async () => {
    try {
      const deliveryData = webhookManager.getDeliveries(selectedWebhook?.id);
      setDeliveries(deliveryData);
    } catch (error) {
      console.error('Failed to load deliveries:', error);
    }
  }, [selectedWebhook?.id]);

  const handleCreateWebhook = async () => {
    try {
      const webhookData = {
        ...webhookForm,
        integrationId,
        events: availableEvents.filter(event => webhookForm.events.includes(event.type))
      };

      await webhookManager.registerWebhook(webhookData);
      setShowCreateModal(false);
      resetForm();
      loadWebhooks();
    } catch (error) {
      console.error('Failed to create webhook:', error);
      alert('Failed to create webhook: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const _handleUpdateWebhook = async () => {
    if (!selectedWebhook) return;

    try {
      await webhookManager.updateWebhook(selectedWebhook.id, {
        ...webhookForm,
        events: availableEvents.filter(event => webhookForm.events.includes(event.type))
      });
      setShowEditModal(false);
      setSelectedWebhook(null);
      resetForm();
      loadWebhooks();
    } catch (error) {
      console.error('Failed to update webhook:', error);
      alert('Failed to update webhook: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleDeleteWebhook = async (webhookId: string) => {
    const ok = await confirm({
      title: 'Delete this webhook?',
      description: 'Events will stop being delivered to this endpoint immediately. Deliveries already queued are not retried.',
      confirmLabel: 'Delete webhook',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await webhookManager.deleteWebhook(webhookId);
      loadWebhooks();
    } catch (error) {
      console.error('Failed to delete webhook:', error);
      alert('Failed to delete webhook: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleToggleWebhook = async (webhook: WebhookEndpoint) => {
    try {
      await webhookManager.updateWebhook(webhook.id, { active: !webhook.active });
      loadWebhooks();
    } catch (error) {
      console.error('Failed to toggle webhook:', error);
    }
  };

  const handleTestWebhook = async (webhook: WebhookEndpoint) => {
    setSelectedWebhook(webhook);
    setShowTestModal(true);
  };

  const handleViewDelivery = (delivery: WebhookDelivery) => {
    setSelectedDelivery(delivery);
    setShowDeliveryModal(true);
  };

  const runWebhookTest = async () => {
    if (!selectedWebhook) return;

    setTesting(true);
    try {
      const result = await webhookManager.testWebhook(selectedWebhook.id);
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        responseTime: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setTesting(false);
    }
  };

  const resetForm = () => {
    setWebhookForm({
      name: '',
      url: '',
      events: [],
      active: true,
      secret: '',
      headers: {},
      retryPolicy: {
        maxAttempts: 5,
        backoffMultiplier: 2,
        initialDelay: 1000,
        maxDelay: 300000,
        retryOn: [408, 429, 500, 502, 503, 504]
      },
      filters: [],
      transformations: []
    });
  };

  const openEditModal = (webhook: WebhookEndpoint) => {
    setSelectedWebhook(webhook);
    setWebhookForm({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events.map(e => e.type),
      active: webhook.active,
      secret: webhook.secret,
      headers: webhook.headers || {},
      retryPolicy: webhook.retryPolicy,
      filters: webhook.filters || [],
      transformations: webhook.transformations || []
    });
    setShowEditModal(true);
  };

  const getStatusColor = (status) => tone(toneForStatus(status)).chip;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'retrying': return <RefreshCw className="w-4 h-4 animate-spin" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const filteredWebhooks = webhooks.filter(webhook => {
    const matchesSearch = webhook.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         webhook.url.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && webhook.active) ||
                         (statusFilter === 'inactive' && !webhook.active);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-300" />
        <span className="ml-2 text-content-secondary">Loading webhooks...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-content">Webhook Management</h1>
            {agentMode && (
              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium flex items-center gap-1">
                <Bot className="w-4 h-4" />
                Agent API
              </span>
            )}
          </div>
          <p className="text-content-secondary mt-1">
            {agentMode 
              ? 'Configure webhooks for AI agent events and real-time notifications'
              : 'Configure and monitor webhook endpoints for real-time integration events'
            }
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Webhook
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-surface p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-secondary">Total Webhooks</p>
              <p className="text-2xl font-bold text-content">{webhooks.length}</p>
            </div>
            <Globe className="w-8 h-8 text-blue-600 dark:text-blue-300" />
          </div>
        </div>

        <div className="bg-surface p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-secondary">Active Webhooks</p>
              <p className="text-2xl font-bold text-content">
                {webhooks.filter(w => w.active).length}
              </p>
            </div>
            <Zap className="w-8 h-8 text-green-600 dark:text-green-300" />
          </div>
        </div>

        <div className="bg-surface p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-secondary">Total Deliveries</p>
              <p className="text-2xl font-bold text-content">
                {webhooks.reduce((sum, w) => sum + w.deliveryStats.totalDeliveries, 0)}
              </p>
            </div>
            <Activity className="w-8 h-8 text-purple-600 dark:text-purple-300" />
          </div>
        </div>

        <div className="bg-surface p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-secondary">Success Rate</p>
              <p className="text-2xl font-bold text-content">
                {webhooks.length > 0 
                  ? Math.round(webhooks.reduce((sum, w) => sum + w.deliveryStats.successRate, 0) / webhooks.length)
                  : 0}%
              </p>
            </div>
            <BarChart3 className="w-8 h-8 text-indigo-600 dark:text-indigo-300" />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-line mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'webhooks', label: 'Webhooks', icon: Globe },
            { id: 'deliveries', label: 'Deliveries', icon: Activity },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            ...(agentMode ? [{ id: 'agents', label: 'Agent Events', icon: Bot }] : [])
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                    : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'webhooks' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-content-subtle" />
                <input
                  type="text"
                  placeholder="Search webhooks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-line-strong rounded-lg w-full"
                />
              </div>
            </div>
            <SelectField
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 border border-line-strong rounded-lg"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </SelectField>
          </div>

          {/* Webhooks List */}
          <div className="bg-surface rounded-lg shadow-sm border overflow-hidden">
            {filteredWebhooks.length === 0 ? (
              <div className="p-8 text-center">
                <Globe className="w-12 h-12 text-content-subtle mx-auto mb-4" />
                <h3 className="text-lg font-medium text-content mb-2">No Webhooks Found</h3>
                <p className="text-content-secondary mb-4">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'No webhooks match your current filters.'
                    : 'Create your first webhook to start receiving real-time notifications.'
                  }
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Create Webhook
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredWebhooks.map((webhook) => (
                  <div key={webhook.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-content">{webhook.name}</h3>
                          <div className="flex items-center gap-2">
                            {webhook.active ? (
                              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded text-xs font-medium flex items-center gap-1">
                                <Unlock className="w-3 h-3" />
                                Active
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200 rounded text-xs font-medium flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                Inactive
                              </span>
                            )}
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded text-xs font-medium">
                              {webhook.events.length} events
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 mb-3">
                          <ExternalLink className="w-4 h-4 text-content-subtle" />
                          <span className="text-sm text-content-secondary font-mono">{webhook.url}</span>
                          <button
                            onClick={() => navigator.clipboard.writeText(webhook.url)}
                            className="text-content-subtle hover:text-content-secondary"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                          <div>
                            <p className="text-sm text-content-secondary">Success Rate</p>
                            <p className="font-medium">{webhook.deliveryStats.successRate.toFixed(1)}%</p>
                          </div>
                          <div>
                            <p className="text-sm text-content-secondary">Total Deliveries</p>
                            <p className="font-medium">{webhook.deliveryStats.totalDeliveries}</p>
                          </div>
                          <div>
                            <p className="text-sm text-content-secondary">Last Delivery</p>
                            <p className="font-medium">
                              {webhook.lastDelivery ? formatTimeAgo(webhook.lastDelivery) : 'Never'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-content-secondary">Avg Response</p>
                            <p className="font-medium">{webhook.deliveryStats.averageResponseTime.toFixed(0)}ms</p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1">
                          {webhook.events.slice(0, 3).map((event) => (
                            <span key={event.type} className="px-2 py-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                              {event.type}
                            </span>
                          ))}
                          {webhook.events.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                              +{webhook.events.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleTestWebhook(webhook)}
                          className="text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-300 p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30"
                          title="Test webhook"
                        >
                          <TestTube className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleWebhook(webhook)}
                          className={`p-2 rounded ${
                            webhook.active 
                              ? 'text-yellow-600 dark:text-yellow-300 hover:text-yellow-800 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/30' 
                              : 'text-green-600 dark:text-green-300 hover:text-green-800 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30'
                          }`}
                          title={webhook.active ? 'Disable webhook' : 'Enable webhook'}
                        >
                          {webhook.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => openEditModal(webhook)}
                          className="text-content-secondary hover:text-gray-800 dark:hover:text-gray-200 p-2 rounded hover:bg-gray-50 dark:hover:bg-dark-700"
                          title="Edit webhook"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteWebhook(webhook.id)}
                          className="text-red-600 dark:text-red-300 hover:text-red-800 dark:hover:text-red-300 p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                          title="Delete webhook"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'deliveries' && (
        <div className="space-y-6">
          {/* Deliveries Table */}
          <div className="bg-surface rounded-lg shadow-sm border overflow-hidden">
            <div className="px-6 py-4 border-b border-line">
              <h3 className="text-lg font-small text-content">Recent Deliveries</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-dark-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Webhook
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Event
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Attempts
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Response
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-gray-200">
                  {deliveries.map((delivery) => {
                    const webhook = webhooks.find(w => w.id === delivery.webhookId);
                    return (
                      <tr key={delivery.id} className="hover:bg-gray-50 dark:hover:bg-dark-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-small text-content">
                            {webhook?.name || 'Unknown'}
                          </div>
                          <div className="text-sm text-content-muted font-mono">
                            {webhook?.url.substring(0, 40)}...
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-small bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200 rounded">
                            {delivery.eventType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-small flex items-center gap-1 w-fit ${getStatusColor(delivery.status)}`}>
                            {getStatusIcon(delivery.status)}
                            {delivery.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                          {delivery.attempts}
                          {delivery.nextRetry && (
                            <div className="text-xs text-content-muted">
                              Next: {formatTimeAgo(delivery.nextRetry)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                          {delivery.responseStatus && (
                            <span className={`px-2 py-1 rounded text-xs ${
                              delivery.responseStatus >= 200 && delivery.responseStatus < 300
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                            }`}>
                              {delivery.responseStatus}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-content-muted">
                          {formatTimeAgo(delivery.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-small">
                          <button 
                            onClick={() => handleViewDelivery(delivery)}
                            className="text-blue-600 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-300"
                            title="View delivery details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Analytics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-surface p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-content-secondary">Delivery Success Rate</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-300">
                    {webhooks.length > 0 
                      ? Math.round(webhooks.reduce((sum, w) => sum + w.deliveryStats.successRate, 0) / webhooks.length)
                      : 0}%
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-300" />
              </div>
              <div className="mt-2">
                <span className="text-xs text-content-muted">
                  {webhooks.reduce((sum, w) => sum + w.deliveryStats.successfulDeliveries, 0)} successful deliveries
                </span>
              </div>
            </div>

            <div className="bg-surface p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-content-secondary">Failed Deliveries</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-300">
                    {webhooks.reduce((sum, w) => sum + w.deliveryStats.failedDeliveries, 0)}
                  </p>
                </div>
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-300" />
              </div>
              <div className="mt-2">
                <span className="text-xs text-content-muted">
                  {webhooks.length > 0 
                    ? Math.round(webhooks.reduce((sum, w) => sum + (100 - w.deliveryStats.successRate), 0) / webhooks.length)
                    : 0}% failure rate
                </span>
              </div>
            </div>

            <div className="bg-surface p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-small text-content-secondary">Avg Response Time</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-300">
                    {webhooks.length > 0 
                      ? Math.round(webhooks.reduce((sum, w) => sum + w.deliveryStats.averageResponseTime, 0) / webhooks.length)
                      : 0}ms
                  </p>
                </div>
                <Clock className="w-8 h-8 text-blue-600 dark:text-blue-300" />
              </div>
              <div className="mt-2">
                <span className="text-xs text-content-muted">
                  Across all webhooks
                </span>
              </div>
            </div>

            <div className="bg-surface p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-small text-content-secondary">Retry Rate</p>
                  <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-300">
                    {deliveries.length > 0 
                      ? Math.round((deliveries.filter(d => d.attempts > 1).length / deliveries.length) * 100)
                      : 0}%
                  </p>
                </div>
                <RefreshCw className="w-8 h-8 text-yellow-600 dark:text-yellow-300" />
              </div>
              <div className="mt-2">
                <span className="text-xs text-content-muted">
                  {deliveries.filter(d => d.attempts > 1).length} deliveries retried
                </span>
              </div>
            </div>
          </div>

          {/* Performance Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Delivery Status Distribution */}
            <div className="bg-surface p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-content mb-4">Delivery Status Distribution</h3>
              <div className="space-y-3">
                {['delivered', 'failed', 'pending', 'retrying'].map((status) => {
                  const count = deliveries.filter(d => d.status === status).length;
                  const percentage = deliveries.length > 0 ? (count / deliveries.length) * 100 : 0;
                  return (
                    <div key={status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(status)}
                        <span className="text-sm font-small capitalize">{status}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              status === 'delivered' ? 'bg-green-500' :
                              status === 'failed' ? 'bg-red-500' :
                              status === 'pending' ? 'bg-yellow-500' :
                              'bg-blue-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-content-secondary w-12 text-right">
                          {count}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Event Type Distribution */}
            <div className="bg-surface p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-content mb-4">Event Type Distribution</h3>
              <div className="space-y-3">
                {availableEvents.slice(0, 6).map((event) => {
                  const count = deliveries.filter(d => d.eventType === event.type).length;
                  const percentage = deliveries.length > 0 ? (count / deliveries.length) * 100 : 0;
                  return (
                    <div key={event.type} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full" />
                        <span className="text-sm font-small">{event.type}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-blue-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-content-secondary w-12 text-right">
                          {count}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Webhook Performance Table */}
          <div className="bg-surface rounded-lg shadow-sm border overflow-hidden">
            <div className="px-6 py-4 border-b border-line">
              <h3 className="text-lg font-semibold text-content">Webhook Performance</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-dark-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                      Webhook
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Success Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Total Deliveries
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Avg Response Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Last Delivery
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-small text-content-muted uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-gray-200">
                  {webhooks.map((webhook) => (
                    <tr key={webhook.id} className="hover:bg-gray-50 dark:hover:bg-dark-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-small text-content">{webhook.name}</div>
                        <div className="text-sm text-content-muted font-mono truncate max-w-xs">
                          {webhook.url}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-1">
                            <div className="w-16 bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  webhook.deliveryStats.successRate >= 90 ? 'bg-green-500' :
                                  webhook.deliveryStats.successRate >= 70 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${webhook.deliveryStats.successRate}%` }}
                              />
                            </div>
                          </div>
                          <span className="ml-2 text-sm font-small">
                            {webhook.deliveryStats.successRate.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                        {webhook.deliveryStats.totalDeliveries}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                        {webhook.deliveryStats.averageResponseTime.toFixed(0)}ms
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-content-muted">
                        {webhook.lastDelivery ? formatTimeAgo(webhook.lastDelivery) : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {webhook.active ? (
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded text-xs font-small flex items-center gap-1 w-fit">
                            <CheckCircle className="w-3 h-3" />
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200 rounded text-xs font-small flex items-center gap-1 w-fit">
                            <XCircle className="w-3 h-3" />
                            Inactive
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Activity Timeline */}
          <div className="bg-surface rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-line">
              <h3 className="text-lg font-semibold text-content">Recent Activity</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {deliveries.slice(0, 10).map((delivery, _index) => {
                  const webhook = webhooks.find(w => w.id === delivery.webhookId);
                  return (
                    <div key={delivery.id} className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                        delivery.status === 'delivered' ? 'bg-green-500' :
                        delivery.status === 'failed' ? 'bg-red-500' :
                        delivery.status === 'pending' ? 'bg-yellow-500' :
                        'bg-blue-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-small text-content">
                            {webhook?.name || 'Unknown Webhook'}
                          </p>
                          <p className="text-sm text-content-muted">
                            {formatTimeAgo(delivery.createdAt)}
                          </p>
                        </div>
                        <p className="text-sm text-content-secondary">
                          {delivery.eventType} - {delivery.status}
                          {delivery.responseStatus && ` (${delivery.responseStatus})`}
                        </p>
                        {delivery.attempts > 1 && (
                          <p className="text-xs text-content-muted">
                            Attempted {delivery.attempts} times
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
                {deliveries.length === 0 && (
                  <div className="text-center py-8">
                    <Activity className="w-12 h-12 text-content-subtle mx-auto mb-4" />
                    <p className="text-content-secondary">No delivery activity yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agent Events Tab (only shown in agent mode) */}
      {activeTab === 'agents' && agentMode && (
        <div className="space-y-6">
          <div className="bg-surface rounded-lg shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-4">
              <Bot className="w-6 h-6 text-blue-600 dark:text-blue-300" />
              <h3 className="text-lg font-semibold text-content">AI Agent Events</h3>
            </div>
            <p className="text-content-secondary mb-6">
              Configure webhooks to receive real-time notifications about AI agent activities and API interactions.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {availableEvents
                .filter(event => event.type.startsWith('agent.'))
                .map((event) => (
                  <div key={event.type} className="border border-line rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-content">{event.type}</h4>
                        <p className="text-sm text-content-secondary mt-1">{event.description}</p>
                      </div>
                      <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded text-xs font-medium">
                        Agent
                      </span>
                    </div>
                  </div>
                ))}
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Key className="w-5 h-5 text-blue-600 dark:text-blue-300 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-300">Agent Authentication Required</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Agent event webhooks require proper authentication and will include agent identification 
                    in the payload. Make sure your webhook endpoint can handle agent-specific data.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Webhook Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-line">
              <h2 className="text-xl font-semibold text-content">Create Webhook</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  value={webhookForm.name}
                  onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="My Webhook"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL</label>
                <input
                  type="url"
                  value={webhookForm.url}
                  onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                  className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://your-app.com/webhooks/endpoint"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Events</label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-line rounded-lg p-3">
                  {availableEvents.map((event) => (
                    <label key={event.type} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={webhookForm.events.includes(event.type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setWebhookForm({
                              ...webhookForm,
                              events: [...webhookForm.events, event.type]
                            });
                          } else {
                            setWebhookForm({
                              ...webhookForm,
                              events: webhookForm.events.filter(t => t !== event.type)
                            });
                          }
                        }}
                        className="rounded border-line-strong text-blue-600 dark:text-blue-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{event.type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Secret (optional)</label>
                <input
                  type="text"
                  value={webhookForm.secret}
                  onChange={(e) => setWebhookForm({ ...webhookForm, secret: e.target.value })}
                  className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Leave empty to auto-generate"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  checked={webhookForm.active}
                  onChange={(e) => setWebhookForm({ ...webhookForm, active: e.target.checked })}
                  className="rounded border-line-strong text-blue-600 dark:text-blue-300 focus:ring-blue-500"
                />
                <label htmlFor="active" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Active (start receiving events immediately)
                </label>
              </div>
            </div>
            <div className="p-6 border-t border-line flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-700 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateWebhook}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Webhook
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Webhook Modal */}
      {showTestModal && selectedWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-line">
              <h2 className="text-xl font-semibold text-content">Test Webhook</h2>
              <p className="text-sm text-content-secondary mt-1">{selectedWebhook.name}</p>
            </div>
            <div className="p-6">
              <p className="text-sm text-content-secondary mb-4">
                This will send a test payload to your webhook endpoint to verify it's working correctly.
              </p>
              
              {testResult && (
                <div className={`p-4 rounded-lg mb-4 ${
                  testResult.success ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50' : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {testResult.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-300" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
                    )}
                    <span className={`font-medium ${testResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                      {testResult.success ? 'Test Successful' : 'Test Failed'}
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    <p><strong>Response Time:</strong> {testResult.responseTime}ms</p>
                    {testResult.responseStatus && (
                      <p><strong>Status Code:</strong> {testResult.responseStatus}</p>
                    )}
                    {testResult.error && (
                      <p><strong>Error:</strong> {testResult.error}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-line flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setTestResult(null);
                  setSelectedWebhook(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-700 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600"
              >
                Close
              </button>
              <button
                onClick={runWebhookTest}
                disabled={testing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {testing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <TestTube className="w-4 h-4" />
                    Run Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Details Modal */}
      {showDeliveryModal && selectedDelivery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-line">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-content">Delivery Details</h2>
                  <p className="text-sm text-content-secondary mt-1">
                    {webhooks.find(w => w.id === selectedDelivery.webhookId)?.name || 'Unknown Webhook'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowDeliveryModal(false);
                    setSelectedDelivery(null);
                  }}
                  className="text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status and Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-content mb-3">Delivery Status</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(selectedDelivery.status)}`}>
                          {getStatusIcon(selectedDelivery.status)}
                          {selectedDelivery.status.charAt(0).toUpperCase() + selectedDelivery.status.slice(1)}
                        </span>
                      </div>
                      <div className="text-sm text-content-secondary">
                        <p><strong>Event Type:</strong> {selectedDelivery.eventType}</p>
                        <p><strong>Attempts:</strong> {selectedDelivery.attempts}</p>
                        <p><strong>Created:</strong> {selectedDelivery.createdAt.toLocaleString()}</p>
                        {selectedDelivery.deliveredAt && (
                          <p><strong>Delivered:</strong> {selectedDelivery.deliveredAt.toLocaleString()}</p>
                        )}
                        {selectedDelivery.nextRetry && (
                          <p><strong>Next Retry:</strong> {selectedDelivery.nextRetry.toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-content mb-3">Response Details</h3>
                    <div className="space-y-2 text-sm text-content-secondary">
                      {selectedDelivery.responseStatus && (
                        <p>
                          <strong>Status Code:</strong> 
                          <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                            selectedDelivery.responseStatus >= 200 && selectedDelivery.responseStatus < 300
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                          }`}>
                            {selectedDelivery.responseStatus}
                          </span>
                        </p>
                      )}
                      {selectedDelivery.lastAttempt && (
                        <p><strong>Last Attempt:</strong> {selectedDelivery.lastAttempt.toLocaleString()}</p>
                      )}
                      {selectedDelivery.error && (
                        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg">
                          <p className="font-medium text-red-800 dark:text-red-300">Error Message:</p>
                          <p className="text-red-700 dark:text-red-300 mt-1">{selectedDelivery.error}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Webhook URL */}
              <div>
                <h3 className="text-lg font-medium text-content mb-3">Webhook Endpoint</h3>
                <div className="bg-gray-50 dark:bg-dark-700 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-content-subtle" />
                    <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                      {webhooks.find(w => w.id === selectedDelivery.webhookId)?.url || 'Unknown URL'}
                    </span>
                    <button
                      onClick={() => {
                        const url = webhooks.find(w => w.id === selectedDelivery.webhookId)?.url;
                        if (url) navigator.clipboard.writeText(url);
                      }}
                      className="text-content-subtle hover:text-content-secondary"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Payload */}
              <div>
                <h3 className="text-lg font-medium text-content mb-3">Payload</h3>
                <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                  <pre className="text-sm">
                    {JSON.stringify(selectedDelivery.payload, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Response Body */}
              {selectedDelivery.responseBody && (
                <div>
                  <h3 className="text-lg font-medium text-content mb-3">Response Body</h3>
                  <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-sm">
                      {selectedDelivery.responseBody}
                    </pre>
                  </div>
                </div>
              )}

              {/* Response Headers */}
              {selectedDelivery.responseHeaders && Object.keys(selectedDelivery.responseHeaders).length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-content mb-3">Response Headers</h3>
                  <div className="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
                    <div className="space-y-1 text-sm">
                      {Object.entries(selectedDelivery.responseHeaders).map(([key, value]) => (
                        <div key={key} className="flex">
                          <span className="font-medium text-gray-700 dark:text-gray-300 w-32 flex-shrink-0">{key}:</span>
                          <span className="text-content-secondary">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-line flex justify-end">
              <button
                onClick={() => {
                  setShowDeliveryModal(false);
                  setSelectedDelivery(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-700 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookManagementDashboard;